/*
  React wrapper around d3-org-chart.

  The chart library is kept rather than reimplemented: its layout, zoom/pan and expand-collapse
  behaviour are tuned, byte-identical between the two legacy apps, and the drag-to-re-parent
  behaviour alone has a 433-line test suite behind it. Replacing it is a project in its own right.

  d3-org-chart is imperative and owns its own DOM, so this component is the boundary: React decides
  *what* data and selection exist, the library decides how it is laid out. Data changes go through
  .data().render() rather than remounting, which is what preserves the user's zoom and expansion
  state across an edit — including an edit that arrived from another user.
*/
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import { OrgChart as D3OrgChart } from 'd3-org-chart';
import type { Bucket, NodeDisplay, Perspective, PresenceMember } from '@integrate/contracts';
import {
  currentBucket,
  emptyComposition,
  fmt,
  fmtMoneyCompact,
  getEmpField,
  isDimmed,
  stageMarksOf,
  stageOutlineOf,
  type StageMark,
  type StageOutline,
  ROOT_ID,
  type ChartNode,
  type Composition,
  type SeparationState,
  type StageCounts,
  type ViewLens,
} from '@integrate/core';

/** What the root card says it is showing when it is not showing a head. */
const PERSPECTIVE_META: Record<Perspective, string> = {
  'as-is': 'As-is view',
  remain: 'RemainCo view',
  'carve-out': 'Carve-out view',
};

export interface OrgChartProps {
  /**
   * The whole design, lens-free.
   *
   * The engine takes a state and a lens, and it is the caller's job to hold them separately — the app spent
   * P2 removing every place that swapped a lens into shared state and put it back afterwards.
   */
  state: SeparationState;
  /**
   * The tree, already built.
   *
   * Passed in rather than computed here because the group panel needs the same nodes — a group's
   * headcount, FTE and cost are rollups the hierarchy already worked out — and building it twice would
   * be the same pure function run twice over the whole roster on every render.
   */
  nodes: readonly ChartNode[];
  rootLabel: string;
  /**
   * The lens the tree was built under, passed rather than reconstructed.
   *
   * Two things need it. A card's values resolve through the PERSPECTIVE: they used to be read straight
   * off the record while the person panel edited them through `setEmpField`, so renaming somebody in a
   * Carve-out view changed the panel and left the card showing the old value — two surfaces
   * disagreeing about the same person, with nothing on screen saying which was the override. And
   * `isDimmed` needs the whole lens, because greying is a function of perspective, stage and view mode
   * together.
   */
  lens: ViewLens;
  /**
   * What the cards show. PROJECT state, not a lens: the deck exporter reads the same config, and the
   * promise that a slide shows what the screen shows only holds if there is one answer per project.
   */
  nodeDisplay: NodeDisplay;
  selectedId: string | null;
  /**
   * The multi-selection, as EMPLOYEE ids.
   *
   * Deliberately NOT in the render effect's dependency list. That effect ends in `data().render()`, which
   * destroys and recreates every card element — a full relayout per ctrl-click. The set is read from the
   * ref inside `renderCard` for a fresh render, and a separate small effect keyed on the set alone
   * repaints the existing cards in one DOM pass. That is what the frozen app does too.
   */
  selected: ReadonlySet<string>;
  /** Plain clicks select instead of opening a panel. */
  selectMode: boolean;
  members: readonly PresenceMember[];
  onSelect(nodeId: string | null): void;
  /** A NODE id — the caller resolves it to people, at whatever scope the node implies. */
  onToggleSelect(nodeId: string): void;
}



/**
 * A chart node plus the presentation values the card needs.
 *
 * Flattened onto the datum deliberately: the render callback is a string template called once per
 * node per frame, so resolving the union there — person vs group vs root, each with a different
 * `data` shape — would put narrowing logic in the hottest, least testable place in the component.
 */
/**
 * Exported for the card tests, which are the only assertion this repo has on a raw-HTML sink.
 *
 * `renderCard` builds the node card as an HTML STRING and the chart assigns it with `innerHTML`, so
 * every interpolated value — names, job titles, ids, level values — is untrusted spreadsheet content
 * arriving at a sink. That is worth testing directly rather than through a browser: a component test
 * can hand it a hostile id and parse what comes out, which is the actual claim, while the e2e can only
 * check that a benign fixture logged no errors.
 */
export interface ChartDatum {
  id: string;
  parentId: string;
  kind: ChartNode['kind'];
  label: string;
  _bucket: Bucket | null;
  _sub: string;
  /**
   * Filled headcount for a group — and for the ROOT, which used to carry null.
   *
   * `_headcount ?? 0` then rendered "0 people" on the organisation's own box for every project on the
   * platform, which is not a missing total, it is a wrong one. The root's total is the sum of its
   * group children, computed from the same nodes the group cards report.
   */
  _headcount: number | null;
  _salary: number;
  _fte: number;
  /**
   * How many of `_headcount` this card is actually about, when that differs.
   *
   * null when it does not — under `hide` every member is in scope, so saying "12 of 12" would be noise.
   * Under `grey` the out-of-scope people are in the group and in its salary, its FTE and its
   * composition, so a card reading "180 people / €14.2m" is describing an organisation the reader is
   * not looking at, and the stage line beneath it is population-independent, so the card contradicts
   * itself.
   */
  _inScope: number | null;
  /** The second meta line's chips, already resolved and gated by the display config. */
  _meta: string[];
  /**
   * The stage and stranded chips, decided in core (`stageMarksOf`) rather than here.
   *
   * Their own row rather than more entries in `_meta`, and not because of taste: `.n-meta` is a single
   * `nowrap` line with `text-overflow: ellipsis`, shared with the group and root cards, so a chip
   * appended there would be truncated off the right edge of a 220px card while every string-level
   * assertion still passed. Widening `.n-meta` would change the height of every group card for an
   * unrelated reason.
   */
  _marks: StageMark[];
  /** The card's one outline slot — a group card reads it off its designated head. */
  _stageOutline: StageOutline;
  /**
   * Bucket split as proportions, or null when the config or the data says draw nothing.
   *
   * Segments rather than a number, because the point of the bar is the shape: a group that is 90%
   * Remain and one that is 90% Carve-out read differently at a glance in a way two counts do not.
   */
  _bar: { cls: string; pct: number; colour: string; label: string }[] | null;
  /** One pre-rendered line per enabled metric, each comparing the enabled stages. */
  _stageLines: { metric: string; parts: string }[];
  /** This person's employee id, or null on a group or the root. */
  _employeeId: string | null;
  /**
   * Every employee id at or below a group or root card, so it can badge how many are selected.
   *
   * Built bottom-up ONCE per tree rather than per card: walking each group's subtree independently would
   * be O(groups × people) on every render, and the badge is the cheap part of this feature.
   */
  _subtreeIds: readonly string[];
  /** A manager's subtree, in the reporting layout. Null everywhere else. */
  _roll: { reports: number; salary: number; fte: number } | null;
  /** Out of scope for this perspective but shown rather than removed — `viewMode: 'grey'`. */
  _dimmed: boolean;
  _peerNames: string[];
  /**
   * d3-org-chart reads this to decide a node's initial expansion, and mutates it in place when the
   * user toggles a branch. Carrying it across data updates is what keeps the tree from collapsing.
   */
  _expanded?: boolean;
}

/**
 * Exported for the tests, and worth its own assertion despite being five lines.
 *
 * The AMPERSAND MUST GO FIRST. Escaping `<` to `&lt;` and then escaping ampersands would turn it into
 * `&amp;lt;`, so the card would print the entity rather than the character — a bug that looks like a
 * rendering glitch and is actually the escaping order.
 */
export function escapeHtml(value: unknown): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function bucketClass(bucket: Bucket | null): string {
  return bucket ? `bucket-${bucket.replace(/\s+/g, '-')}` : '';
}

/*
  The stage-comparison line's vocabulary, ported verbatim from STAGE_LINE_STAGES / STAGE_LINE_METRICS
  (orgchart.html:4281-4287). Order matters: it is the order the line reads in.
*/
const STAGE_LINE_STAGES: { key: keyof StageCounts; short: string; label: string }[] = [
  { key: 'asis', short: 'A', label: 'As-is (whole organisation)' },
  { key: 'p', short: 'P', label: 'Perimeter' },
  { key: 'd1', short: 'D1', label: 'Day 1' },
  { key: 'd2', short: 'D2', label: 'Day 2' },
  { key: 'r1', short: 'R1', label: 'RemainCo after Day 1 (existing people)' },
  { key: 'rs', short: 'RS', label: 'RemainCo steady state (after planned rightsizings)' },
];

const STAGE_LINE_METRICS: {
  key: 'hc' | 'fte' | 'cost';
  short: string;
  format(v: number): string;
}[] = [
  { key: 'hc', short: 'HC', format: (v) => fmt(v) },
  { key: 'fte', short: 'FTE', format: (v) => fmt(v, 1) },
  { key: 'cost', short: '€', format: (v) => fmtMoneyCompact(v) },
];

/**
 * The bucket split as bar segments. Ported from `bucketBarHtml` (orgchart.html:4145-4165).
 *
 * Colours come from the display config — the same palette the deck uses — so a palette edit flows to
 * both. Open positions and inactive people get fixed slate tones, as they do in the app: they are not
 * buckets and have no configured colour.
 */
function barSegments(
  composition: Composition,
  colours: NodeDisplay['colors'],
): ChartDatum['_bar'] {
  const parts: { cls: string; count: number; colour: string; label: string }[] = [
    { cls: 'seg-remain', count: composition.Remain, colour: colours.status.Remain, label: 'Remain' },
    { cls: 'seg-remain-bu', count: composition.RemainBu, colour: colours.status.Remain, label: 'Remain build-up' },
    { cls: 'seg-carve', count: composition['Carve-out'], colour: colours.status['Carve-out'], label: 'Carve-out' },
    { cls: 'seg-carve-bu', count: composition.CarveBu, colour: colours.status['Carve-out'], label: 'Carve-out build-up' },
    { cls: 'seg-nolong', count: composition['No longer required'], colour: colours.status['No longer required'], label: 'No longer required' },
    { cls: 'seg-open', count: composition.Open, colour: '64748B', label: 'Open positions' },
    { cls: 'seg-inactive', count: composition.Inactive, colour: '94A3B8', label: 'Inactive' },
  ];
  const total = parts.reduce((n, x) => n + x.count, 0);
  if (!total) return null;
  return parts
    .filter((x) => x.count > 0)
    .map((x) => ({
      cls: x.cls,
      pct: (x.count / total) * 100,
      colour: x.colour,
      label: `${x.label} ${Math.round((x.count / total) * 100)}% (${fmt(x.count)})`,
    }));
}

/**
 * One line per enabled metric, comparing the enabled stages. Ported from `stageCountsHtml` (:4288).
 *
 * Both halves have to be non-empty or there is nothing to compare, which is why the app returns early
 * on either — a metric with no stages is a label and no numbers.
 */
function stageLinesOf(
  counts: StageCounts | null,
  config: NodeDisplay['stageLine'],
): ChartDatum['_stageLines'] {
  if (!counts) return [];
  const stages = STAGE_LINE_STAGES.filter((s) => config[s.key as keyof NodeDisplay['stageLine']]);
  const metrics = STAGE_LINE_METRICS.filter((m) => config[m.key]);
  if (!stages.length || !metrics.length) return [];
  return metrics.map((m) => ({
    metric: m.short,
    parts: stages.map((s) => `${s.short} ${m.format(counts[s.key][m.key])}`).join(' · '),
  }));
}

/**
 * How tall a card has to be for what it is about to draw.
 *
 * Derived rather than fixed, because the display config decides how many lines there are: leaving the
 * height constant would clip a crowded card and leave a gap under a sparse one. The same reasoning as
 * `personCardHeight` / `groupCardHeight` (orgchart.html:4307-4335), at this card's smaller scale.
 */
function cardHeight(d: ChartDatum): number {
  if (d.kind === 'person') {
    let h = 40;
    if (d._sub) h += 15;
    if (d._meta.length) h += 15;
    if (d._marks.length) h += 15;
    if (d._bucket) h += 15;
    if (d._roll) h += 30;
    return h;
  }
  let h = 40;
  if (d._headcount !== null) h += 16;
  if (d._meta.length) h += 15;
  if (d._stageLines.length) h += 13 * d._stageLines.length;
  if (d._bar) h += 12;
  return h;
}

/** Exported for the tests — see the note on `ChartDatum`. Not used outside this module otherwise. */
export function renderCard(d: ChartDatum, selectedId: string | null, selected: ReadonlySet<string>): string {
  const classes = ['node-card', bucketClass(d._bucket)];
  if (d.kind === 'group') classes.push('is-group');
  if (d.kind === 'root') classes.push('is-root');
  if (d._stageOutline) classes.push(`is-${d._stageOutline}`);
  if (d.id === selectedId) classes.push('selected');
  if (d._peerNames.length) classes.push('peer-selected');
  /*
    Three cues, three CSS properties, so all three can be true at once — see the note in styles.css. A
    person card is selected or not; a group or root card reports how many of its subtree are, which is what
    makes a COLLAPSED branch honest and closes a real hole in the frozen app, where the repaint only ever
    queried person cards so a group with its whole team selected looked identical to an empty one.
  */
  const selectedUnder =
    d.kind === 'person' ? null : d._subtreeIds.reduce((a, id) => a + (selected.has(id) ? 1 : 0), 0);
  /*
    The RING means "all of this is selected"; the BADGE means "this many under here are".

    Ringing anything with a selected descendant put a ring on every ancestor up to the organisation's own
    card, so selecting one team lit a path through the whole chart and the cue stopped meaning anything.
    A fully selected group reads as one object, which is what the gesture that produced it did; a partially
    selected ancestor is just a place to look, and the count says where.
  */
  const wholeSubtree =
    d.kind !== 'person' && d._subtreeIds.length > 0 && selectedUnder === d._subtreeIds.length;
  if (d.kind === 'person' ? selected.has(d._employeeId ?? '') : wholeSubtree) {
    classes.push('multi-sel');
  }
  // Out of scope for this perspective, kept on screen rather than removed. Without the class the grey
  // mode would silently change the population behind every number and mark none of it.
  if (d._dimmed) classes.push('dimmed');

  // Every interpolated value is escaped: names, job titles and level values all originate in an
  // uploaded spreadsheet, which is untrusted input.
  const title = escapeHtml(d.label);

  // data-* hooks are for the end-to-end tests: asserting on rendered markup is how the legacy
  // suites verify node cards too, and it survives layout changes that a CSS selector would not.
  const attrs =
    `data-testid="node" data-node-id="${escapeHtml(d.id)}" data-kind="${escapeHtml(d.kind)}"` +
    ` data-bucket="${escapeHtml(d._bucket ?? '')}" data-peers="${d._peerNames.length}"` +
    ` data-dimmed="${d._dimmed ? 1 : 0}"` +
    (d.kind === 'person'
      ? ` data-employee-id="${escapeHtml(d._employeeId ?? '')}"` +
        ` data-selected="${selected.has(d._employeeId ?? '') ? 1 : 0}"`
      : ` data-selected-under="${selectedUnder ?? 0}"`) +
    (d._headcount === null ? '' : ` data-headcount="${d._headcount}"`) +
    (d._inScope === null ? '' : ` data-in-scope="${d._inScope}"`) +
    // Exact-value rather than a token list, so `[data-stage-outline="day2exit"]` works with the same
    // idiom every other hook here uses instead of needing an attribute-contains selector.
    ` data-stage-outline="${d._stageOutline ?? ''}"`;

  const meta = d._meta.length
    ? `<div class="n-meta">${d._meta.map((m) => escapeHtml(m)).join(' · ')}</div>`
    : '';

  /*
    The stage and stranded chips. Text and title are ESCAPED: the '·TSA' tooltip carries an agreement's
    name, and an agreement's name is typed by a user into a register.
  */
  const marks = d._marks.length
    ? `<div class="n-marks" data-testid="node-marks">` +
      d._marks
        .map(
          (m) =>
            `<span class="n-mark n-mark-${m.tone}" data-mark="${escapeHtml(m.kind)}"` +
            ` title="${escapeHtml(m.title)}">${escapeHtml(m.text)}</span>`,
        )
        .join('') +
      `</div>`
    : '';

  /*
    The stage-comparison line ignores the stage lens on purpose and respects the filters — the app says
    so in its own tooltip (`:4294`), and it is the reason the line can disagree with the headline above
    it: one is the stage you are looking at, the other is every stage at once.
  */
  const stageLines = d._stageLines
    .map(
      (line) =>
        `<div class="n-stage" data-testid="node-stage-line" title="Stage comparison — ignores the current stage lens, respects filters">` +
        `<strong>${escapeHtml(line.metric)}</strong> ${escapeHtml(line.parts)}</div>`,
    )
    .join('');

  const bar = d._bar
    ? `<div class="n-bar" data-testid="node-composition" title="${escapeHtml(d._bar.map((seg) => seg.label).join(' · '))}">` +
      d._bar
        .map(
          (seg) =>
            `<span class="${seg.cls}" style="width:${seg.pct.toFixed(2)}%;background-color:#${escapeHtml(seg.colour)}"></span>`,
        )
        .join('') +
      '</div>'
    : '';

  if (d.kind === 'person') {
    // A manager in the reporting layout gets a left rule, as the app does (`:575`).
    if (d._roll) classes.push('is-manager');
    const sub = escapeHtml(d._sub);
    const peers = d._peerNames.length
      ? `<div class="n-sub" style="color:var(--c-pink)">\u{1F441} ${escapeHtml(d._peerNames.join(', '))}</div>`
      : '';
    return `<div class="${classes.join(' ')}" ${attrs}>
      <div class="n-name">${title}</div>
      ${sub ? `<div class="n-sub">${sub}</div>` : ''}
      ${meta}
      ${marks}
      ${d._bucket ? `<div class="n-count" data-testid="node-bucket">${escapeHtml(d._bucket)}</div>` : ''}
      ${
        d._roll
          ? `<div class="n-roll" data-testid="node-rollup" data-reports="${d._roll.reports}">` +
            `<span class="hd">Reports</span> ${fmt(d._roll.reports)} · ${fmt(d._roll.fte, 1)} FTE · ` +
            `${escapeHtml(fmtMoneyCompact(d._roll.salary))}</div>`
          : ''
      }
      ${peers}
    </div>`;
  }

  // `_headcount === null` means this card was told not to show a total — not that the total is zero.
  const count =
    d._headcount === null
      ? ''
      : d._inScope === null
        ? `<div class="n-count">${d._headcount} ${d._headcount === 1 ? 'person' : 'people'}</div>`
        : `<div class="n-count" data-testid="node-in-scope">${d._inScope} of ${d._headcount} in scope</div>`;
  /*
    THE SAME EYE LINE THE PERSON BRANCH DRAWS, and its absence here was a half-built affordance rather
    than a decision. `_peerNames` is populated for every node kind and the `peer-selected` class and
    `data-peers` attribute are set for every kind — so a group or root card a colleague had selected drew
    the pink ring with nothing to say who was inside it. Group ids reach presence like any other:
    `onNodeClick` fires for every kind and nothing in the contract or the server restricts it.
  */
  const groupPeers = d._peerNames.length
    ? `<div class="n-sub" style="color:var(--c-pink)">\u{1F441} ${escapeHtml(d._peerNames.join(', '))}</div>`
    : '';
  return `<div class="${classes.join(' ')}" ${attrs}>
    <div class="n-name">${title}</div>
    ${count}
    ${meta}
    ${stageLines}
    ${bar}
    ${groupPeers}
  </div>`;
}

/** Imperative controls the surrounding React tree needs but cannot express declaratively. */
export interface OrgChartHandle {
  expandAll(): void;
  collapseAll(): void;
  fit(): void;
}

export const OrgChart = forwardRef<OrgChartHandle, OrgChartProps>(function OrgChart(props, ref) {
  const container = useRef<HTMLDivElement>(null);
  const chart = useRef<D3OrgChart<ChartDatum> | null>(null);
  /** The datum objects handed to the chart last time; the library mutates `_expanded` on them. */
  const lastData = useRef<ChartDatum[]>([]);
  // Held in a ref so the render callback always sees current values without rebuilding the chart.
  const latest = useRef(props);
  latest.current = props;

  /*
    Employee ids beneath every node, accumulated bottom-up in one pass and shared by both effects.

    The render effect needs them to badge a fresh card; the repaint effect needs them to update an existing
    one without a relayout. Emitting them as a `data-` attribute instead would put every employee id in the
    project into one attribute on the root card, so the index stays in JavaScript and the DOM carries only
    the count.

    Post-order, so each node unions its children's sets once they are complete. A head belongs to their own
    group's set — `buildHierarchy` draws them on the group card rather than as a leaf, which is the rule
    `subtreePeople` in core is built around.
  */
  const subtreeIds = useMemo(() => {
    const kidsOf = new Map<string, ChartNode[]>();
    for (const n of props.nodes) {
      const list = kidsOf.get(n.parentId) ?? [];
      list.push(n);
      kidsOf.set(n.parentId, list);
    }
    const out = new Map<string, string[]>();
    const collect = (id: string): string[] => {
      const cached = out.get(id);
      if (cached) return cached;
      const acc: string[] = [];
      for (const child of kidsOf.get(id) ?? []) {
        if (child.kind === 'person') acc.push(String(child.empId));
        else if (child.data.head) acc.push(String(child.data.head.id));
        acc.push(...collect(child.id));
      }
      out.set(id, acc);
      return acc;
    };
    for (const n of props.nodes) if (n.kind !== 'person') collect(n.id);
    return out;
  }, [props.nodes]);

  useEffect(() => {
    const nodes = props.nodes;

    // Which nodes other people are looking at, so presence shows up on the chart itself.
    const peersByNode = new Map<string, string[]>();
    for (const member of props.members) {
      if (!member.selectedId) continue;
      const list = peersByNode.get(member.selectedId) ?? [];
      list.push(member.name);
      peersByNode.set(member.selectedId, list);
    }

    // Carry expansion across the rebuild.
    //
    // Every re-render hands d3-org-chart a fresh datum array, and the library reads expansion from
    // the datum — so without this, any edit (including one arriving from another user) collapses the
    // tree and throws away where the person was working. The legacy app solves the same problem the
    // same way, by remembering which node ids were expanded.
    const wasExpanded = new Map<string, boolean>();
    for (const previous of lastData.current) {
      if (previous._expanded !== undefined) wasExpanded.set(previous.id, previous._expanded);
    }

    const display = props.nodeDisplay;
    const perspective = props.lens.perspective;
    const overrides = props.state.design.scenarioOverrides;

    /*
      The organisation's own total.

      `RootData` carries no count, and the card used to render `_headcount ?? 0` — so every project on
      the platform printed "0 people" on its root box. Summed from the group children rather than from
      the roster, so it reports the same population the cards beneath it do: whatever the lens has
      narrowed to, and nothing it has hidden.
    */
    const rootTotal = nodes.reduce(
      (n, node) => (node.kind === 'group' && node.parentId === ROOT_ID ? n + node.data.count : n),
      0,
    );
    const rootPeople = nodes.filter((n) => n.kind === 'person' && n.parentId === ROOT_ID).length;

    /*
      The organisation's own bucket split, summed from its group children.

      The app walks the roster instead (`orgchart.html:4321-4331`), re-applying `isVisible` and
      `passesFilters` as it goes. Summing the children reaches the same answer through the tree the lens
      already produced, which means it cannot disagree with the cards beneath it — and it costs nothing.
      Under the reporting layout there are no group children, so the bar is empty there; the app has the
      same hole, because it has no root card in that layout either.
    */
    const rootComposition = nodes.reduce((acc, node) => {
      if (node.kind !== 'group' || node.parentId !== ROOT_ID) return acc;
      for (const key of Object.keys(acc) as (keyof Composition)[]) {
        acc[key] += node.data.composition[key] ?? 0;
      }
      return acc;
    }, emptyComposition());

    const data: ChartDatum[] = nodes.map((n) => {
      // Narrowed once, at the top: the union's `data` differs per kind, and resolving it inside a
      // string template would put the narrowing in the least testable place in the component.
      const person = n.kind === 'person' ? n.data : null;
      const employeeId = n.kind === 'person' ? n.empId : null;
      const rootHead = n.kind === 'root' ? n.data.head : null;
      const groupNode = n.kind === 'group' ? n.data : null;

      /*
        Resolved through the perspective, not read off the record.

        A scenario override written from the person panel changed the panel and left the card showing
        the old value. `getEmpField` is the one funnel every other reader goes through.
      */
      const label =
        n.id === ROOT_ID
          ? props.rootLabel
          : person
            ? String(getEmpField(person, 'name', perspective, overrides) ?? n.label)
            : n.label;

      const meta: string[] = [];
      if (person) {
        if (display.person.salary) {
          const salary = Number(getEmpField(person, 'salary', perspective, overrides)) || 0;
          if (salary) meta.push(fmtMoneyCompact(salary));
        }
        if (display.person.fte) {
          const fte = Number(getEmpField(person, 'fte', perspective, overrides));
          if (Number.isFinite(fte) && fte !== 1) meta.push(`${fmt(fte, 2)} FTE`);
        }
        if (display.person.dedication && person.dedication != null) {
          meta.push(`${Math.round(person.dedication * 100)}% ded.`);
        }
        if (display.person.location) {
          const location = String(getEmpField(person, 'location', perspective, overrides) ?? '');
          if (location) meta.push(location);
        }
      } else if (groupNode) {
        if (display.group.levelTag && groupNode.levelLabel) meta.push(groupNode.levelLabel);
        if (display.group.salary && groupNode.salary) meta.push(fmtMoneyCompact(groupNode.salary));
        if (display.group.fte) meta.push(`${fmt(groupNode.fte, 1)} FTE`);
        if (display.group.headOf && groupNode.head) {
          meta.push(`★ ${String(getEmpField(groupNode.head, 'name', perspective, overrides) ?? '')}`);
        }
      } else {
        if (display.root.headOf && rootHead) {
          meta.push(`★ ${String(getEmpField(rootHead, 'name', perspective, overrides) ?? '')}`);
        } else {
          meta.push(PERSPECTIVE_META[perspective]);
        }
      }

      const datum: ChartDatum = {
        id: n.id,
        parentId: n.parentId,
        kind: n.kind,
        label,
        _bucket:
          person && employeeId && display.person.status
            ? currentBucket(props.state.design, employeeId)
            : null,
        _sub:
          person && display.person.jobTitle
            ? String(getEmpField(person, 'jobTitle', perspective, overrides) ?? '')
            : '',
        // null means "this card was told not to show a total", which is not the same as zero.
        _headcount: groupNode
          ? display.group.headcount
            ? groupNode.count
            : null
          : n.kind === 'root' && display.root.headcount
            ? rootTotal + rootPeople
            : null,
        _salary: groupNode ? groupNode.salary : 0,
        _fte: groupNode ? groupNode.fte : 0,
        // Only when it differs, and only where the engine computed it.
        _inScope:
          groupNode && display.group.headcount && groupNode.inScope !== groupNode.count
            ? groupNode.inScope
            : null,
        _bar:
          groupNode && display.group.compositionBar
            ? barSegments(groupNode.composition, display.colors)
            : n.kind === 'root' && display.root.compositionBar
              ? barSegments(rootComposition, display.colors)
              : null,
        _stageLines: groupNode
          ? display.group.stageCounts
            ? stageLinesOf(groupNode.stageCounts, display.stageLine)
            : []
          : n.kind === 'root' && display.root.stageCounts
            ? stageLinesOf(n.data.stageCounts, display.stageLine)
            : [],
        /*
          A manager's subtree, and only in the reporting layout — `_roll` is attached there and only
          when somebody actually reports to them (`hierarchy.ts`), so this is null on a leaf.
        */
        _roll: display.person.rollup && n.kind === 'person' && n._roll ? n._roll : null,
        _employeeId: employeeId === null ? null : String(employeeId),
        _subtreeIds: n.kind === 'person' ? [] : (subtreeIds.get(n.id) ?? []),
        _meta: meta,
        /*
          `props.state.design`, NOT `_bucket`.

          `_bucket` is set to null whenever `display.person.status` is off — that toggle hides the bucket
          PILL — so gating the markers on it would make unticking "Carve-out / Remain status" silently
          delete every carve-side chip and both outlines, while leaving the stranded chips (whose gate
          does not touch the bucket) in place. The frozen app computes the bucket unconditionally and
          suppresses only the class (`orgchart.html:4381-4382`). Nothing in any suite would catch the
          port version: the legacy suites never touch `person.status`, and parity runs at the defaults.
        */
        _marks: person ? stageMarksOf(props.state.design, person, display.person.stageMarkers) : [],
        _stageOutline: person
          ? stageOutlineOf(props.state.design, person, display.person.stageMarkers)
          : // A group card wears its designated head's outline, and only while heads are shown at all.
            groupNode?.head && display.group.headOf
            ? stageOutlineOf(props.state.design, groupNode.head, display.person.stageMarkers)
            : null,
        // Dimming is cosmetic and only ever true under `viewMode: 'grey'` — `isDimmed` says so, and it
        // is the engine's answer rather than a second one derived here.
        _dimmed: employeeId ? isDimmed(props.state, props.lens, employeeId) : false,
        _peerNames: peersByNode.get(n.id) ?? [],
        /*
          Carried for PEOPLE too, which the grouping layout never needed and the reporting layout is
          made of. `buildReportingHierarchy` sets the flag on every person deliberately — its own
          comment says "so the flag is never left undefined" — to open the tree two levels deep, and
          d3-org-chart collapses everything on each render and re-expands only the nodes carrying it.
          Dropping it for people therefore discarded the whole computed default expansion, in the one
          layout where every node is a person. Inert in the grouping layout, where people have no flag.
        */
        _expanded: n._expanded,
      };
      const expanded = wasExpanded.get(n.id);
      if (expanded !== undefined) datum._expanded = expanded;
      return datum;
    });
    lastData.current = data;

    if (!container.current) return;

    if (!chart.current) {
      chart.current = new D3OrgChart<ChartDatum>()
        .container(container.current)
        .nodeWidth(() => 220)
        .childrenMargin(() => 50)
        .compactMarginBetween(() => 18)
        .siblingsMargin(() => 20)
        .initialZoom(0.7)
        .layout('top')
        .nodeContent((node) =>
          renderCard(node.data as ChartDatum, latest.current.selectedId, latest.current.selected),
        )
        .onNodeClick((node) => {
          // d3-org-chart hands back either the datum or its id depending on version; accept both.
          const id =
            typeof node === 'string'
              ? node
              : ((node as { data?: { id?: string }; id?: string }).data?.id ??
                (node as { id?: string }).id ??
                null);
          latest.current.onSelect(id ?? null);
        });
    }

    /*
      Re-applied every run, not set once at construction.

      Card height is a function of the display config — how many lines this card is about to draw — so
      pinning it inside the create-once block would clip a crowded card and leave a gap under a sparse
      one the moment a toggle changed. The app derives it the same way (orgchart.html:4307-4335).
    */
    chart.current.nodeHeight((node) => cardHeight(node.data as ChartDatum));

    chart.current.data(data).render();
    // fit() is deliberately not called on every render: it would reset the user's pan and zoom
    // every time anyone on the project made a change.
    /*
      `nodeDisplay` and the lens are dependencies, not just ref reads.

      `renderCard` is only ever called from `.data().render()` inside this effect, so a config the
      effect does not depend on would sit in the ref and change nothing until the next unrelated edit —
      a checkbox that appears to do nothing until somebody moves a person.
    */
  }, [
    props.nodes,
    props.state,
    props.members,
    props.rootLabel,
    props.selectedId,
    props.nodeDisplay,
    props.lens,
    subtreeIds,
  ]);

  useImperativeHandle(
    ref,
    () => ({
      // d3-org-chart owns expansion state, so these are pass-throughs rather than React state.
      expandAll: () => chart.current?.expandAll().fit(),
      collapseAll: () => chart.current?.collapseAll().fit(),
      fit: () => chart.current?.fit(),
    }),
    [],
  );

  useEffect(() => {
    return () => {
      chart.current = null;
      if (container.current) container.current.innerHTML = '';
    };
  }, []);

  /*
    Repaint the selection cues in place, without a relayout.

    The render effect above ends in `data().render()`, which destroys and recreates every card element — so
    keying it on the selection would mean a full layout pass per ctrl-click, and the chart visibly jumping.
    One DOM pass over the existing cards instead, which is what the frozen app does.

    This is only safe because the render effect no longer fires on unrelated frames: `restyleForeignObject`
    rewrites every card's innerHTML on each render, so anything toggled outside React's knowledge would be
    wiped mid-interaction. `peers` and `cards` are memoised in App for exactly this reason.

    A group card's badge counts its whole subtree, which is what makes a COLLAPSED branch honest: the frozen
    app only ever repainted person cards, so a group with its entire team selected looked like an empty one.
  */
  useEffect(() => {
    const root = container.current;
    if (!root) return;
    const selected = props.selected;
    for (const el of root.querySelectorAll<HTMLElement>('[data-node-id]')) {
      const kind = el.dataset.kind;
      if (kind === 'person') {
        const on = selected.has(String(el.dataset.employeeId ?? ''));
        el.classList.toggle('multi-sel', on);
        el.dataset.selected = on ? '1' : '0';
      } else {
        const ids = subtreeIds.get(String(el.dataset.nodeId)) ?? [];
        const n = ids.reduce((acc: number, id: string) => acc + (selected.has(id) ? 1 : 0), 0);
        // Same rule as renderCard: ring only a WHOLE subtree, badge any part of one.
        el.classList.toggle('multi-sel', ids.length > 0 && n === ids.length);
        el.dataset.selectedUnder = String(n);
      }
    }
  }, [props.selected, props.nodes, props.nodeDisplay, props.lens, subtreeIds]);

  /*
    A selection click, intercepted on the way DOWN.

    d3-org-chart calls `onNodeClick(node)` with one argument and binds it bubble-phase, so the handler the
    wrapper installs above cannot see a modifier key — the event never reaches it. The card DOM is a string
    template the library injects, so no React handler can be attached to a card either.

    React delegates at the root container, which is an ancestor of the library's `g.node`, so a
    capture-phase handler here runs BEFORE the library's bubble-phase listener and `stopPropagation` can
    pre-empt it. Keeping it as a React prop rather than an `addEventListener` keeps it inside the lifecycle
    with no cleanup effect to forget.

    Shift is a toggle, not a range extend: the frozen app folds it in with ctrl and meta, and there is no
    anchor or "last clicked" concept anywhere in it to extend from.
  */
  const onClickCapture = (event: React.MouseEvent<HTMLDivElement>): void => {
    const p = latest.current;
    if (!p.selectMode && !event.ctrlKey && !event.metaKey && !event.shiftKey) return;
    const card = (event.target as HTMLElement).closest?.('[data-node-id]') as HTMLElement | null;
    const nodeId = card?.dataset.nodeId;
    if (!nodeId) return;
    event.stopPropagation();
    event.preventDefault();
    p.onToggleSelect(nodeId);
  };

  return <div className="chart-area" ref={container} onClickCapture={onClickCapture} />;
});
